import { Chart as ChartJS, LineElement, BarElement, TimeScale } from "chart.js";
import "chartjs-adapter-date-fns";
import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Row,
} from "shards-react";

import {
  getAccuracyByPersonType,
  getAccuracyBySexAndWeight,
  getNumberOfOpinionsByPersonType,
  getSemaphore,
  getTotalAccuracy,
} from "../../../backend/opinions";
import moment from "moment";
import BovinePrice from "../../common/BovinePrice";
import WeeklyDatePicker from "../../common/WeeklyDatePicker";

ChartJS.register(BarElement, LineElement, TimeScale);

const six_months = moment()
  .subtract(6, "months")
  .startOf("isoWeek")
  .format("DD-MM-YYYY");
const three_months = moment()
  .subtract(3, "months")
  .startOf("isoWeek")
  .format("DD-MM-YYYY");
const one_month = moment()
  .subtract(1, "months")
  .startOf("isoWeek")
  .format("DD-MM-YYYY");
const this_week = moment().startOf("isoWeek").format("DD-MM-YYYY");

const Overview = () => {
  // Initialize it as the monday of the last week
  const [selectedDate, setSelectedDate] = useState(this_week);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);

  const [totalAccuracy, setTotalAccuracy] = useState("--");
  const [accuracyByPersonType, setAccuracyByPersonType] = useState([]);
  const [accuracyBySexAndWeight, setAccuracyBySexAndWeight] = useState([]);
  const [semaphoreMale, setSemaphoreMale] = useState(undefined);
  const [semaphoreFemale, setSemaphoreFemale] = useState(undefined);
  const [opinionsByPersonType, setOpinionsByPersonType] = useState([]);

  const getTotalAccuracy1 = async () => {
    const accuracy = await getTotalAccuracy(selectedDate, toDate);
    setTotalAccuracy(accuracy?.toFixed(1) || "--");
  };

  const getAccuracyByPersonType1 = async () => {
    const accuracy = await getAccuracyByPersonType(selectedDate, toDate);
    setAccuracyByPersonType(accuracy);
  };

  const getAccuracyBySexAndWeight1 = async () => {
    const accuracy = await getAccuracyBySexAndWeight(selectedDate, toDate);
    setAccuracyBySexAndWeight(accuracy);
  };

  const getSemaphore1 = async () => {
    const semaphore = await getSemaphore(selectedDate, toDate);
    // Filter by male and female
    setSemaphoreMale(semaphore?.find((data) => data._id.sex === "male"));
    setSemaphoreFemale(semaphore?.find((data) => data._id.sex === "female"));
    setLoading(false);
  };

  const getNumberOfOpinionsByPersonType1 = async () => {
    const response = await getNumberOfOpinionsByPersonType(
      selectedDate,
      toDate
    );
    setOpinionsByPersonType(response);
  };

  useEffect(() => {
    setLoading(true);
    getTotalAccuracy1();
    getAccuracyByPersonType1();
    getAccuracyBySexAndWeight1();
    getSemaphore1();
    getNumberOfOpinionsByPersonType1();
  }, [selectedDate]);

  return (
    <Container
      fluid
      className="p-4"
      style={{
        minHeight: "90vh",
        overflow: "hidden",
        maxWidth: 1400,
      }}
    >
      <Row
        className="mb-3"
        style={{
          paddingLeft: 10,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 30,
          rowGap: 10,
        }}
      >
        <h3 className="m-0" style={{ alignSelf: "end" }}>
          Opiniones
        </h3>
        <div>
          <label style={{ display: "block", margin: 0 }}>Acumulado</label>
          <ButtonGroup style={{ flexWrap: "wrap", marginRight: 10 }}>
            <Button
              onClick={() => {
                setSelectedDate(six_months);
                setToDate(null);
              }}
              outline={selectedDate !== six_months}
              disabled={loading}
            >
              6 meses
            </Button>
            <Button
              onClick={() => {
                setSelectedDate(three_months);
                setToDate(null);
              }}
              outline={selectedDate !== three_months}
              disabled={loading}
            >
              3 meses
            </Button>
            <Button
              onClick={() => {
                setSelectedDate(one_month);
                setToDate(null);
              }}
              outline={selectedDate !== one_month}
              disabled={loading}
            >
              1 mes
            </Button>
            <Button
              onClick={() => {
                setSelectedDate(this_week);
                setToDate(null);
              }}
              outline={selectedDate !== this_week}
              disabled={loading}
            >
              Esta semana
            </Button>
          </ButtonGroup>
        </div>
        <div>
          <label style={{ display: "block", margin: 0 }}>Semana concreta</label>
          <WeeklyDatePicker
            date={moment(selectedDate, "DD-MM-YYYY")}
            changeDate={(newDate) => {
              setSelectedDate(newDate.startOf("isoWeek").format("DD-MM-YYYY"));
              setToDate(newDate.endOf("isoWeek").format("DD-MM-YYYY"));
            }}
            loading={loading}
          />
        </div>
      </Row>
      <Alert
        fade
        style={{ backgroundColor: "#dddddd", color: "black", marginTop: 10 }}
      >
        La <span style={{ color: "#046ecb" }}>PRECISIÓN</span> mostrada se
        calcula con la diferencia del valor absoluto entre la opinión y el
        precio real.
      </Alert>
      {/* <SumUp /> */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Card
          style={{
            boxShadow: "0 0 10px 0 rgba(100,110,140,.2)",
            padding: "10px 40px",
            width: "100%",
          }}
        >
          <Row>
            <div>
              <h4>
                Precisión{" "}
                <span style={{ fontWeight: "bold", fontSize: 17 }}>
                  ({"< 0.03"})
                </span>
              </h4>
              Media total: <h2>{totalAccuracy}%</h2>
            </div>
            <Col
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                overflow: "auto",
                minWidth: 200,
              }}
            >
              <table
                className="table-mid mb-0"
                style={{ margin: "0 auto", fontSize: 17 }}
              >
                <thead className="bg-light">
                  <tr>
                    <th scope="col" className="border-0"></th>
                    <th scope="col" className="border-0">
                      Macho
                    </th>
                    <th scope="col" className="border-0"></th>
                    <th scope="col" className="border-0">
                      Hembra
                    </th>
                    <th scope="col" className="border-0"></th>
                    <th scope="col" className="border-0">
                      Frisón
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{"<280KG"}</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "<280KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>180/220KG</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "180/220KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>{"<220KG"}</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "<220KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                  </tr>
                  <tr>
                    <td>281/320KG</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "281/320KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>221/260KG</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "221/260KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>{">220KG"}</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === ">220KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                  </tr>
                  <tr>
                    <td>321/370KG</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "321/370KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>261/300KG</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === "261/300KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                  </tr>
                  <tr>
                    <td>{">371KG"}</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === ">371KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                    <td>{">300KG"}</td>
                    <td>
                      {accuracyBySexAndWeight?.find(
                        (row) => row._id.weight === ">300KG"
                      )?.accuracy ?? "-"}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
            <div style={{ height: "100%", maxHeight: 200, maxWidth: 180 }}>
              <Bar
                data={{
                  labels: ["Com.", "Prod.", "IA"],
                  datasets: [
                    {
                      backgroundColor: [
                        "rgba(75,192,192,0.7)",
                        "rgba(255,99,132,0.7)",
                        "rgba(255,206,86,0.7)",
                      ],
                      borderWidth: 1,
                      hoverBackgroundColor: [
                        "rgba(75,192,192,0.6)",
                        "rgba(255,206,86,0.6)",
                        "rgba(255,99,132,0.6)",
                      ],
                      hoverBorderColor: "rgba(75,192,192,1)",
                      maxBarThickness: 50,
                      data: [
                        accuracyByPersonType
                          ?.find(
                            (person) =>
                              person._id.personType === "comercializador"
                          )
                          ?.accuracy?.toFixed(1) || 0,
                        accuracyByPersonType
                          ?.find(
                            (person) => person._id.personType === "productor"
                          )
                          ?.accuracy?.toFixed(1) || 0,
                        accuracyByPersonType
                          ?.find((person) => person._id.personType === "IA")
                          ?.accuracy?.toFixed(1) || 0,
                      ],
                    },
                  ],
                }}
                options={{
                  plugins: {
                    annotation: {
                      annotations: {
                        line: {
                          type: "line",
                          yMin: totalAccuracy,
                          yMax: totalAccuracy,
                          borderColor: "grey",
                          borderWidth: 1,
                          borderDash: [5, 3],
                        },
                      },
                    },
                    title: {
                      display: true,
                      text: "Precisión media",
                      font: {
                        size: 16,
                      },
                    },
                    legend: {
                      display: false,
                    },
                    datalabels: {
                      font: {
                        size: 14,
                        weight: "bold",
                      },
                    },
                  },
                  scales: {
                    x: {
                      grid: {
                        display: false,
                      },
                      ticks: {
                        font: {
                          size: 14,
                        },
                      },
                    },
                    y: {
                      display: false,
                      grid: {
                        display: false,
                      },
                      ticks: {
                        font: {
                          size: 14,
                        },
                      },
                    },
                  },
                  maintainAspectRatio: false,
                }}
              />
            </div>
          </Row>
        </Card>
        <Card
          className="p-2"
          style={{
            boxShadow: "0 0 10px 0 rgba(100,110,140,.2)",
            width: "100%",
          }}
        >
          {/* <h4>Semáforo</h4> */}
          <div style={{ height: 200 }}>
            <Bar
              data={{
                labels: [
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "O3=",
                  "O3=",
                ],
                datasets: [
                  {
                    backgroundColor: "#F9360B",
                    data: semaphoreMale?.data?.map((data, index) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round((data.countLessThanMinus3 / total) * 100)
                        : 0;
                    }),
                  },
                  {
                    backgroundColor: "#FFF033",
                    data: semaphoreMale?.data?.map((data, index) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round(
                            (data.countBetweenMinus3And3 / total) * 100
                          )
                        : 0;
                    }),
                  },
                  {
                    backgroundColor: "#3ACA00",
                    data: semaphoreMale?.data?.map((data, index) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round((data.countGreaterThan3 / total) * 100)
                        : 0;
                    }),
                  },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                // Remove barThickness and use these spacing options instead
                categoryPercentage: 0.8, // Controls the space between categories (bars)
                barPercentage: 1.0, // Controls the width of bars within a category
                maxBarThickness: 30,

                plugins: {
                  title: {
                    display: true,
                    text: "Machos",
                    font: {
                      size: 16,
                    },
                  },
                  legend: {
                    display: false,
                  },
                  datalabels: {
                    display: false,
                  },
                  tooltip: {
                    mode: "index", // Show tooltip for all datasets at the same x-axis position
                    intersect: false,
                    callbacks: {
                      label: function (context) {
                        return context.parsed.y + "%";
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  secondX: {
                    axis: "x",
                    labels: [
                      "<280KG",
                      "281/320KG",
                      "321/370KG",
                      ">371KG",
                      "<220KG    >220KG",
                    ],
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  y: {
                    display: false,
                    stacked: true,
                    ticks: {
                      min: 0,
                      max: 100,
                      callback: function (value) {
                        return value + "%";
                      },
                      font: {
                        size: 14,
                      },
                    },
                    grid: {
                      display: false,
                    },
                  },
                },
              }}
            />
          </div>
          <div style={{ height: 190, width: "89%" }}>
            <Bar
              data={{
                labels: [
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                  "E3",
                  "U3",
                  "R3",
                  "O3",
                ],
                responsive: true,
                datasets: [
                  {
                    backgroundColor: "#F9360B",
                    data: semaphoreFemale?.data?.map((data) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round((data.countLessThanMinus3 / total) * 100)
                        : 0;
                    }),
                  },
                  {
                    backgroundColor: "#FFF033",
                    data: semaphoreFemale?.data?.map((data) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round(
                            (data.countBetweenMinus3And3 / total) * 100
                          )
                        : 0;
                    }),
                  },
                  {
                    backgroundColor: "#3ACA00",
                    data: semaphoreFemale?.data?.map((data) => {
                      const total =
                        data.countLessThanMinus3 +
                        data.countBetweenMinus3And3 +
                        data.countGreaterThan3;
                      return total > 0
                        ? Math.round((data.countGreaterThan3 / total) * 100)
                        : 0;
                    }),
                  },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                categoryPercentage: 0.8, // Controls the space between categories (bars)
                barPercentage: 1.0, // Controls the width of bars within a category
                maxBarThickness: 30,
                plugins: {
                  title: {
                    display: true,
                    text: "Hembras",
                    font: {
                      size: 15,
                    },
                  },
                  legend: {
                    display: false,
                  },
                  datalabels: {
                    display: false,
                  },
                  tooltip: {
                    mode: "index", // Show tooltip for all datasets at the same x-axis position
                    intersect: false,
                    callbacks: {
                      label: function (context) {
                        return context.parsed.y + "%";
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  secondX: {
                    axis: "x",
                    labels: ["180/220KG", "221/260KG", "261/300KG", ">300KG"],
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  y: {
                    display: false,
                    stacked: true,
                    ticks: {
                      min: 0,
                      max: 100,
                      callback: function (value) {
                        return value + "%";
                      },
                      font: {
                        size: 14,
                      },
                    },
                    grid: {
                      display: false,
                    },
                  },
                },
              }}
            />
          </div>
        </Card>
        <Card
          style={{
            boxShadow: "0 0 10px 0 rgba(100,110,140,.2)",
            width: "100%",
          }}
        >
          <div style={{ height: 300 }}>
            <Bar
              data={{
                labels: [
                  -10, -9, -8, -7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6,
                  7, 8, 9, 10,
                ],
                datasets: [
                  {
                    label: "Comercializadores",
                    backgroundColor: "rgba(75,192,192,0.7)",
                    borderColor: "rgba(75,192,192,1)",
                    borderWidth: 1,
                    hoverBackgroundColor: "rgba(75,192,192,0.9)",
                    hoverBorderColor: "rgba(75,192,192,1)",
                    data: opinionsByPersonType
                      ?.find((person) => person._id === "comercializador")
                      ?.variations?.map((variation) => {
                        return {
                          x: variation.variation,
                          y: variation.numOpinions,
                        };
                      }),
                  },
                  {
                    label: "Productores",
                    backgroundColor: "rgba(255,99,132,0.7)",
                    borderColor: "rgba(255,99,132,1)",
                    borderWidth: 1,
                    hoverBackgroundColor: "rgba(255,99,132,0.9)",
                    hoverBorderColor: "rgba(255,99,132,1)",
                    data: opinionsByPersonType
                      ?.find((person) => person._id === "productor")
                      ?.variations?.map((variation) => {
                        return {
                          x: variation.variation,
                          y: variation.numOpinions,
                        };
                      }),
                  },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    labels: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  datalabels: {
                    display: false,
                  },
                },
                scales: {
                  x: {
                    stacked: false,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                  y: {
                    stacked: false,
                    display: false,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 14,
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </Card>
        <Card style={{ width: "100%", padding: "10px" }}>
          <BovinePrice />
        </Card>
      </div>
    </Container>
  );
};

export default Overview;
